import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { productsStorage } from "@/services/productsStorage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    regular_price: "",
    sale_price: "",
    description: "",
    short_description: "",
    stock_quantity: "",
    sku: "",
    featured: false,
  });

  useEffect(() => {
    if (isEdit && id) {
      loadProduct();
    }
  }, [id, isEdit]);

  const loadProduct = async () => {
    if (!id) return;
    
    const product = await productsStorage.getById(id);
    if (product) {
      setFormData({
        name: product.name,
        price: product.price.toString(),
        regular_price: product.regular_price?.toString() || "",
        sale_price: product.sale_price?.toString() || "",
        description: product.description || "",
        short_description: product.short_description || "",
        stock_quantity: product.stock_quantity.toString(),
        sku: product.sku || "",
        featured: product.featured,
      });

      // Load images
      const productImages = await productsStorage.getProductImages(id);
      setImages(productImages.map((img) => img.src));
    } else {
      toast.error("Produs negăsit");
      navigate("/admin/dashboard");
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setNewImages([...newImages, ...files]);
    }
  };

  const removeNewImage = (index: number) => {
    setNewImages(newImages.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const productData = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        regular_price: formData.regular_price ? parseFloat(formData.regular_price) : undefined,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : undefined,
        description: formData.description.trim(),
        short_description: formData.short_description.trim(),
        stock_quantity: parseInt(formData.stock_quantity),
        sku: formData.sku.trim(),
        featured: formData.featured,
      };

      // Validation
      if (!productData.name) {
        toast.error("Numele produsului este obligatoriu");
        setLoading(false);
        return;
      }
      if (isNaN(productData.price) || productData.price <= 0) {
        toast.error("Prețul trebuie să fie un număr pozitiv");
        setLoading(false);
        return;
      }
      if (isNaN(productData.stock_quantity) || productData.stock_quantity < 0) {
        toast.error("Stocul trebuie să fie un număr pozitiv");
        setLoading(false);
        return;
      }

      let productId: string;

      if (isEdit && id) {
        await productsStorage.update(id, productData);
        productId = id;
        toast.success("Produs actualizat cu succes!");
      } else {
        const newProduct = await productsStorage.create(productData);
        if (!newProduct) throw new Error("Failed to create product");
        productId = newProduct.id;
        toast.success("Produs creat cu succes!");
      }

      // Upload new images
      if (newImages.length > 0) {
        setUploading(true);
        for (let i = 0; i < newImages.length; i++) {
          const imageUrl = await productsStorage.uploadImage(newImages[i], productId);
          if (imageUrl) {
            await productsStorage.addProductImage(productId, imageUrl, images.length + i);
          }
        }
        setUploading(false);
      }

      navigate("/admin/dashboard");
    } catch (error: any) {
      console.error("Error saving product:", error);
      toast.error(error.message || "Eroare la salvarea produsului");
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <div className="p-8">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate("/admin/dashboard")}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Products
      </Button>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="text-2xl font-playfair">
            {isEdit ? "Edit Product" : "Add New Product"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Images */}
            <div className="space-y-3">
              <Label>Product Images</Label>
              
              <div className="grid grid-cols-4 gap-3">
                {images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={img}
                      alt=""
                      className="w-full h-24 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {newImages.map((file, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      className="w-full h-24 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>

              <div>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  disabled={loading || uploading}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Upload multiple images (JPG, PNG, WEBP)
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="price">Price (RON) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="regular_price">Regular Price</Label>
                <Input
                  id="regular_price"
                  type="number"
                  step="0.01"
                  value={formData.regular_price}
                  onChange={(e) => setFormData({ ...formData, regular_price: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sale_price">Sale Price</Label>
                <Input
                  id="sale_price"
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stock_quantity">Stock *</Label>
              <Input
                id="stock_quantity"
                type="number"
                value={formData.stock_quantity}
                onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="short_description">Short Description</Label>
              <Textarea
                id="short_description"
                value={formData.short_description}
                onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                disabled={loading}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                disabled={loading}
                rows={5}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                disabled={loading}
                className="rounded"
              />
              <Label htmlFor="featured" className="cursor-pointer">
                Featured product
              </Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/dashboard")}
                disabled={loading || uploading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || uploading}>
                {loading || uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {uploading ? "Uploading images..." : "Saving..."}
                  </>
                ) : (
                  `${isEdit ? "Update" : "Create"} Product`
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
